import { configureStore } from "@reduxjs/toolkit";
import { persistStore } from "redux-persist";
import { createPersistedReducerForRealm } from "./stateSlice";

/**
* Realm-scoped Redux store factory.
*PURPOSE:
* * The application supports multiple realms ("user" and "carrier") running
* under the same browser origin.
* * redux-persist uses localStorage, which is shared across tabs and realms.
* * A single global store/persist key would cause UI state (tabs, selections,
* in-page memory) to leak between realms.
*
* WHAT THIS DOES:
* * Creates a Redux store bound to a realm-specific persisted reducer.
* * Creates a matching redux-persist persistor.
*
* RESULT:
* user realm    -> persist key "persist:M_State_user"
* carrier realm -> persist key "persist:M_State_carrier"
*
* This guarantees full isolation of persisted UI state between realms.
*/
export function createStoreForRealm(
    realm /* 'user' | 'carrier' */
) {
    // Create a persisted reducer scoped to the given realm
    const reducer = createPersistedReducerForRealm(realm);

    // Configure the Redux store
    const store = configureStore({
        reducer,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                // Disabled because redux-persist and some UI helpers
                // may store non-serializable values intentionally
                serializableCheck: false,
                immutableCheck: false,
            }),
    });

    // Create the persistor bound to this store
    const persistor = persistStore(store);

    return { store, persistor };
}
